import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, Mail, Calendar, Crown, CreditCard, Settings, PenTool, UserCircle } from "lucide-react";
import ImageUpload from "@/writer/components/ImageUpload";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  // Author profile fields
  authorName: z.string().optional(),
  authorBio: z.string().optional(),
  authorWebsite: z.string().url("Invalid website URL").optional().or(z.literal("")),
  authorSocialMedia: z.string().optional(),
  authorContact: z.string().optional(),
  authorPenName: z.string().optional(),
  authorProfessionalCredentials: z.string().optional(),
  authorGenreSpecialization: z.string().optional(),
  authorProfileImage: z.string().optional(),
});

type ProfileForm = z.infer<typeof profileSchema>;

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  role?: string;
  createdAt?: string;
  // Author profile fields
  authorName?: string;
  authorBio?: string;
  authorWebsite?: string;
  authorSocialMedia?: string;
  authorContact?: string;
  authorPenName?: string;
  authorProfessionalCredentials?: string;
  authorGenreSpecialization?: string;
  authorProfileImage?: string;
}

interface CreditStats {
  totalEarned: string;
  totalSpent: string;
  balance: string;
}

export default function ProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: user } = useQuery<{ user: UserData }>({
    queryKey: ["/api/auth/me"],
  });

  const { data: creditStats } = useQuery<CreditStats>({
    queryKey: ["/api/credits/stats"],
  });

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    values: {
      firstName: user?.user?.firstName || "",
      lastName: user?.user?.lastName || "",
      email: user?.user?.email || "",
      // Author profile fields
      authorName: user?.user?.authorName || "",
      authorBio: user?.user?.authorBio || "",
      authorWebsite: user?.user?.authorWebsite || "",
      authorSocialMedia: user?.user?.authorSocialMedia || "",
      authorContact: user?.user?.authorContact || "",
      authorPenName: user?.user?.authorPenName || "",
      authorProfessionalCredentials: user?.user?.authorProfessionalCredentials || "",
      authorGenreSpecialization: user?.user?.authorGenreSpecialization || "",
      authorProfileImage: user?.user?.authorProfileImage || "",
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileForm) => {
      const response = await fetch("/api/user/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error.message || "Failed to update profile",
      });
    },
  });

  const handleSubmit = (data: ProfileForm) => {
    updateProfileMutation.mutate(data);
  };

  const getPlanBadgeColor = (planCode?: string) => {
    switch (planCode) {
      case "FREE": return "bg-gray-100 text-gray-800";
      case "PRO": return "bg-blue-100 text-blue-800";
      case "ENTERPRISE": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </CardTitle>
                <CardDescription>
                  Update your personal details and contact information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        {...form.register("firstName")}
                        placeholder="Enter your first name"
                      />
                      {form.formState.errors.firstName && (
                        <p className="text-red-500 text-sm">{form.formState.errors.firstName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        {...form.register("lastName")}
                        placeholder="Enter your last name"
                      />
                      {form.formState.errors.lastName && (
                        <p className="text-red-500 text-sm">{form.formState.errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      {...form.register("email")}
                      placeholder="Enter your email"
                      disabled
                    />
                    <p className="text-sm text-muted-foreground">
                      Email address cannot be changed. Contact support if you need to update your email.
                    </p>
                    {form.formState.errors.email && (
                      <p className="text-red-500 text-sm">{form.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <ImageUpload
                      label="Profile Image"
                      currentImage={form.watch("authorProfileImage") || null}
                      onImageUpload={(base64Image) => form.setValue("authorProfileImage", base64Image || "")}
                      recommendedSize="400x400px"
                      maxFileSizeMB={2}
                    />
                    <p className="text-sm text-muted-foreground">
                      This image will be used in your author profile and can be included in book covers.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={updateProfileMutation.isPending}
                    className="w-full md:w-auto"
                  >
                    {updateProfileMutation.isPending ? "Updating..." : "Update Profile"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Author Profile */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenTool className="h-5 w-5" />
                  Author Profile
                </CardTitle>
                <CardDescription>
                  Professional author information for book projects and publishing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="authorName">Author Name</Label>
                      <Input
                        id="authorName"
                        {...form.register("authorName")}
                        placeholder="Your professional author name"
                      />
                      <p className="text-xs text-muted-foreground">
                        How you want to be credited as an author
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="authorPenName">Pen Name</Label>
                      <Input
                        id="authorPenName"
                        {...form.register("authorPenName")}
                        placeholder="Alternative author name (optional)"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="authorBio">Author Bio</Label>
                    <Textarea
                      id="authorBio"
                      {...form.register("authorBio")}
                      placeholder="Professional author biography for book publishing..."
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground">
                      This will be used in book back matter and author pages
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="authorWebsite">Website</Label>
                      <Input
                        id="authorWebsite"
                        type="url"
                        {...form.register("authorWebsite")}
                        placeholder="https://yourwebsite.com"
                      />
                      {form.formState.errors.authorWebsite && (
                        <p className="text-red-500 text-sm">{form.formState.errors.authorWebsite.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="authorContact">Contact Email</Label>
                      <Input
                        id="authorContact"
                        type="email"
                        {...form.register("authorContact")}
                        placeholder="author@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="authorSocialMedia">Social Media</Label>
                    <Input
                      id="authorSocialMedia"
                      {...form.register("authorSocialMedia")}
                      placeholder="Twitter, LinkedIn, Instagram handles or links"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="authorGenreSpecialization">Genre Specialization</Label>
                      <Input
                        id="authorGenreSpecialization"
                        {...form.register("authorGenreSpecialization")}
                        placeholder="Fantasy, Romance, Mystery, etc."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="authorProfessionalCredentials">Professional Credentials</Label>
                      <Input
                        id="authorProfessionalCredentials"
                        {...form.register("authorProfessionalCredentials")}
                        placeholder="Awards, publications, qualifications"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Book Structure Studio Integration</h4>
                        <p className="text-sm text-muted-foreground">
                          Author details will be automatically imported into book projects
                        </p>
                      </div>
                      <Button 
                        type="submit" 
                        disabled={updateProfileMutation.isPending}
                        className="w-full md:w-auto"
                      >
                        {updateProfileMutation.isPending ? "Updating..." : "Update Author Profile"}
                      </Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Account Security */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Account Security
                </CardTitle>
                <CardDescription>
                  Manage your account security settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Password</h4>
                    <p className="text-sm text-muted-foreground">Last updated: Never</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Change Password
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Two-Factor Authentication</h4>
                    <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Enable 2FA
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Account Overview */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5" />
                  Account Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Profile</Label>
                  <div className="flex items-center gap-3">
                    {user?.user?.authorProfileImage ? (
                      <img
                        src={user.user.authorProfileImage}
                        alt="Profile"
                        className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                        <UserCircle className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {user?.user?.authorName || `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "No name set"}
                      </p>
                      <p className="text-xs text-muted-foreground">{user?.user?.email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Current Plan</Label>
                  <Badge className={getPlanBadgeColor(user?.user?.planCode)}>
                    {user?.user?.planCode || "FREE"}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Account Type</Label>
                  <Badge variant={user?.user?.role === "admin" ? "default" : "secondary"}>
                    {user?.user?.role === "admin" ? "Administrator" : "User"}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Member Since</Label>
                  <p className="text-sm text-muted-foreground">
                    {user?.user?.createdAt ? new Date(user.user.createdAt).toLocaleDateString() : "Unknown"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Credit Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Current Balance</Label>
                  <p className="text-2xl font-bold text-green-600">
                    {creditStats?.balance ? Number(creditStats.balance).toLocaleString() : 0}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Total Earned</Label>
                    <p className="font-semibold">
                      {creditStats?.totalEarned ? Number(creditStats.totalEarned).toLocaleString() : 0}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Total Spent</Label>
                    <p className="font-semibold">
                      {creditStats?.totalSpent ? Number(creditStats.totalSpent).toLocaleString() : 0}
                    </p>
                  </div>
                </div>

                <Button variant="outline" className="w-full" size="sm">
                  View Credit History
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-muted-foreground">Receive updates via email</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Configure
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}